import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { DataTable } from '@/components/DataTable';
import {
  Button,
  CallStateBadge,
  Card,
  IntentBadge,
  PageHeader,
  ScoreBadge,
  Select,
} from '@/components/ui';
import { formatDateTime, formatDuration, formatMoney, formatPhone } from '@/lib/format';
import { useShowsUsageCosts } from '@/lib/license';

interface CallRow {
  id: string;
  state: string;
  intent: string | null;
  qualified: boolean | null;
  lead_score: number | null;
  caller_number: string | null;
  vicidial_user: string | null;
  vicidial_campaign: string | null;
  vicidial_lead_id: string | null;
  bot_name: string | null;
  /** 'AI' or 'SOUNDBOARD'. A board sits in AI_ACTIVE too; the word is wrong for it. */
  bot_kind: string | null;
  duration_sec: number | null;
  disposition: string | null;
  started_at: string;
  total_client: string | number | null;
}

export default function Calls() {
  const navigate = useNavigate();
  // A client on a flat monthly licence pays nothing per call; the column
  // would be a row of dashes saying so. The API strips the value anyway.
  const showsCosts = useShowsUsageCosts();

  return (
    <div className="w-full">
      <PageHeader
        title="Calls"
        description="Every AI conversation, with its full ownership chain."
        actions={
          <Button
            variant="secondary"
            onClick={() => api.download('/reports/calls.csv')}
          >
            Export CSV
          </Button>
        }
      />

      <CallOutcomes />

      <DataTable<CallRow>
        queryKey="calls"
        path="/calls"
        searchPlaceholder="Phone or lead ID…"
        onRowClick={(call) => navigate(`/calls/${call.id}`)}
        emptyTitle="No calls yet"
        emptyDescription="Calls appear here as soon as the dialer connects a customer to a bot."
        filters={[
          {
            key: 'state',
            label: 'State',
            options: [
              'AI_ACTIVE',
              'QUALIFYING',
              'QUALIFIED',
              'HUMAN_CONNECTED',
              'NOT_INTERESTED',
              'DNC',
              'CALLBACK',
              'FAILED',
              'ENDED',
            ].map((value) => ({
              value,
              // `state` is where a call is NOW. Every finished call is ENDED,
              // so the live-only states match nothing once the call is over --
              // which reads as "this never happens" rather than "this filter
              // asks a different question". Say so, and put the durable one
              // (Transferred, below) underneath it.
              label:
                value === 'HUMAN_CONNECTED'
                  ? 'Human connected (live now)'
                  : value.replace(/_/g, ' '),
            })),
          },
          {
            key: 'intent',
            label: 'Intent',
            options: [
              'QUALIFIED',
              'NOT_INTERESTED',
              'DO_NOT_CALL',
              'WRONG_NUMBER',
              'CALLBACK',
              'NEUTRAL',
            ].map((value) => ({ value, label: value.replace(/_/g, ' ') })),
          },
          {
            key: 'qualified',
            label: 'Qualified',
            options: [
              { value: 'true', label: 'Yes' },
              { value: 'false', label: 'No' },
            ],
          },
          {
            // The durable column, not the state. `state = 'HUMAN_CONNECTED'`
            // above matches only calls still in progress -- every call
            // transitions to ENDED on its way out -- so filtering by it on a
            // day's completed work returns nothing at all.
            key: 'transferred',
            label: 'Transferred',
            options: [
              { value: 'true', label: 'Yes' },
              { value: 'false', label: 'No' },
            ],
          },
          {
            // Where two thirds of a day's dials are machines and dead air,
            // "show me the ones that lasted" is most of what this page is for.
            key: 'min_duration_sec',
            label: 'Longer than',
            options: [
              { value: '20', label: '20 seconds' },
              { value: '30', label: '30 seconds' },
              { value: '60', label: '1 minute' },
              { value: '120', label: '2 minutes' },
            ],
          },
        ]}
        columns={[
          {
            key: 'started_at',
            header: 'Started',
            render: (call) => (
              <span className="whitespace-nowrap text-xs text-ink-500">
                {formatDateTime(call.started_at)}
              </span>
            ),
          },
          {
            key: 'caller',
            header: 'Customer',
            render: (call) => (
              <div>
                <div className="font-mono text-sm">{formatPhone(call.caller_number)}</div>
                {call.vicidial_lead_id && (
                  <div className="text-xs text-ink-500">lead {call.vicidial_lead_id}</div>
                )}
              </div>
            ),
          },
          {
            key: 'bot',
            header: 'Bot / agent',
            render: (call) => (
              <div>
                <div className="text-sm">{call.bot_name ?? '—'}</div>
                <div className="text-xs text-ink-500">
                  {call.vicidial_user ?? '—'}
                  {call.vicidial_campaign && ` · ${call.vicidial_campaign}`}
                </div>
              </div>
            ),
          },
          { key: 'state', header: 'State', render: (call) => <CallStateBadge state={call.state} kind={call.bot_kind} /> },
          { key: 'intent', header: 'Intent', render: (call) => <IntentBadge intent={call.intent} /> },
          {
            key: 'score',
            header: 'Score',
            numeric: true,
            render: (call) => <ScoreBadge score={call.lead_score} />,
          },
          {
            key: 'duration',
            header: 'Duration',
            numeric: true,
            render: (call) => formatDuration(call.duration_sec),
          },
          {
            key: 'disposition',
            header: 'Disposition',
            render: (call) => (
              <span className="font-mono text-xs text-ink-600 dark:text-ink-300">
                {call.disposition ?? '—'}
              </span>
            ),
          },
          ...(showsCosts
            ? [
                {
                  key: 'charge',
                  header: 'Charge',
                  numeric: true,
                  render: (call: CallRow) =>
                    call.total_client === null || call.total_client === undefined ? (
                      <span className="text-ink-400">—</span>
                    ) : (
                      formatMoney(call.total_client)
                    ),
                },
              ]
            : []),
        ]}
      />
    </div>
  );
}

interface Outcomes {
  stats: {
    reached_bot: number;
    human_conversations: number;
    no_reply: number;
    bot_never_spoke: number;
    qualified: number;
    transfers_attempted: number;
    transfers_connected: number;
    avg_duration_sec: number;
    avg_human_duration_sec: number;
    transfer_rate_of_humans: number | null;
    qualify_rate_of_humans: number | null;
    answered_rate: number | null;
  };
  dispositions: { code: string; calls: number }[];
  note: string;
}

/**
 * What the bot achieved, over the denominator that measures the bot.
 *
 * A transfer rate over total dials moves when the list changes; over
 * conversations where someone actually spoke, it moves when the bot does.
 */
function CallOutcomes() {
  const [hours, setHours] = useState(24);

  const { data } = useQuery({
    queryKey: ['call-outcomes', hours],
    queryFn: () => api.get<Outcomes>('/calls/outcomes', { hours }),
    refetchInterval: 60_000,
  });

  const stats = data?.stats;
  const pct = (value: number | null | undefined) =>
    value === null || value === undefined ? '—' : `${value}%`;

  return (
    <Card
      className="mb-4"
      title="Outcomes"
      description="Calls that reached the bot. The dialer attempts many more, so its own totals will be larger."
      actions={
        <Select value={String(hours)} onChange={(e) => setHours(Number(e.target.value))}>
          <option value="1">Last hour</option>
          <option value="24">Last 24 hours</option>
          <option value="168">Last 7 days</option>
          <option value="720">Last 30 days</option>
        </Select>
      }
    >
      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Metric label="Reached the bot" value={stats?.reached_bot ?? 0} />
        <Metric
          label="Human conversations"
          value={stats?.human_conversations ?? 0}
          hint={`${pct(stats?.answered_rate)} of calls`}
        />
        <Metric
          label="Nobody replied"
          value={stats?.no_reply ?? 0}
          hint="bot spoke, no answer"
        />
        <Metric
          label="Qualified"
          value={stats?.qualified ?? 0}
          hint={`${pct(stats?.qualify_rate_of_humans)} of conversations`}
        />
        <Metric
          label="Transferred"
          value={stats?.transfers_connected ?? 0}
          hint={`${pct(stats?.transfer_rate_of_humans)} of conversations`}
          strong
        />
        <Metric
          label="Average call"
          value={formatDuration(stats?.avg_human_duration_sec ?? 0)}
          hint="conversations only"
        />
      </div>

      {(data?.dispositions?.length ?? 0) > 0 && (
        <div className="mt-4">
          <div className="text-xs uppercase tracking-wide text-ink-500">
            Dispositions
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {data?.dispositions.map((row) => (
              <span
                key={row.code}
                className="rounded-md bg-ink-100 px-2 py-1 text-xs dark:bg-ink-800"
              >
                <span className="font-mono">{row.code}</span>
                <span className="ml-1.5 tabular-nums text-ink-500">{row.calls}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {data?.note && (
        <p className="mt-3 text-xs text-ink-500 dark:text-ink-400">{data.note}</p>
      )}
    </Card>
  );
}

function Metric({
  label,
  value,
  hint,
  strong,
}: {
  label: string;
  value: number | string;
  hint?: string;
  strong?: boolean;
}) {
  return (
    <div className="rounded-md border border-line p-3">
      <div className="text-xs text-ink-500">{label}</div>
      <div
        className={`mt-1 text-xl font-semibold tabular-nums ${
          strong ? 'text-brand-600 dark:text-brand-400' : ''
        }`}
      >
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
      {hint && <div className="text-2xs text-ink-500 dark:text-ink-400">{hint}</div>}
    </div>
  );
}
